import { useCourse } from '@/composables/course'
import { useAppSettings } from './useAppSettings'
import { useCountryStore } from '@/composables/geo/useCountryStore'
import { useAccountStore } from '@/stores/account'
import { runSafe } from "./app-init.helpers"
import { useStudentStore } from "@/stores/student"
import { useGoogleAuthStore } from "@/stores/googleAuth"

export function useAppInit(options?: { onSettingsLoaded?: () => void }) {
  const { fetchAllAvailableLocales } = useCourse()
  const { loadSettings } = useAppSettings()

  const applyAppSettings = async () => {
    if (!process.client) return
    await runSafe(async () => {
      await loadSettings()
      options?.onSettingsLoaded?.()
    }, 'Failed to load app settings')
  }

  /**
   * Загружает глобальный список мов курсів (без фільтру по категорії) для хедера.
   * Оновлює globalLocales в course store. Не чіпає allAvailableLocales (category-specific).
   */
  const loadGlobalLocales = () =>
    runSafe(() => fetchAllAvailableLocales(), 'Failed to load global course locales')

  const detectAccount = async () => {
    const { countryIsoCode, setCountry } = useCountryStore()
    const accountStore = useAccountStore()
    const site = useAppConfig().site as { defaultCountryIso?: string, geoDetect?: boolean } | undefined
    const siteIso = site?.defaultCountryIso ?? 'ua'
    await runSafe(async () => {
      // Account routing priority:
      //  - Country-specific sites (geoDetect off, e.g. by/pl/am): ALWAYS route by the
      //    site's own configured ISO — the school is fixed per site, ignore visitor geo.
      //  - Flagship (geoDetect on): route by the geo-detected country, fall back to siteIso.
      // Раніше geo мав пріоритет усюди → заявки з by/pl/am летіли на UA (detect-by-country/ua).
      const primaryIso = (site?.geoDetect ? (countryIsoCode.value || siteIso) : siteIso).toLowerCase()
      const { fallbackUsed } = await accountStore.findAccountByCountry(primaryIso)
      // Locale sites (geoDetect off): force the site's country (phone code + region).
      // Flagship (geoDetect on): keep the visitor's detected country — only locale sites
      // need the override. Overriding on flagship would show Ukrainian regions to everyone
      // whose country has no account (e.g. a UK visitor falls back to UA account but
      // should NOT see Ukrainian oblasts).
      if (fallbackUsed && !site?.geoDetect) setCountry(siteIso.toUpperCase())
    }, 'Failed to detect account')
  }

  const initializeApp = async () => {
    await applyAppSettings()
    const { detectAndStoreCountry } = useCountryStore()
    await detectAndStoreCountry()
    await detectAccount()
    // Глобальний список мов для хедера — не чіпає category-specific стейт
    loadGlobalLocales()
    // Check student auth session — silent if not logged in
    useStudentStore().fetchProfile()
    // Prefetch Google OAuth URL — ready before modal opens
    useGoogleAuthStore().fetchUrl()
  }

  return {
    initializeApp,
    applyAppSettings,
    detectAccount,
    loadGlobalLocales,
  }
}
